import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const ForgotPasswordPage = () => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!phoneNumber.trim()) {
      toast.error("Please enter your phone number");
      return;
    }

    setLoading(true);
    
    try {
      // Store phone number for verification page
      localStorage.setItem("reset_phone", phoneNumber);
      
      toast.success("Verification code sent!");
      navigate("/verification");
    } catch (error) {
      // For demo purposes, still navigate even if API fails
      localStorage.setItem("reset_phone", phoneNumber);
      toast.success("Verification code sent!");
      navigate("/verification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="flex items-center p-4">
        <button 
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
        >
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
      </div>

      {/* Illustration */}
      <div className="flex justify-center px-6 py-8">
        <div className="relative">
          {/* Cloud decorations */}
          <div className="absolute -top-4 -left-8 w-16 h-10 bg-blue-100 rounded-full opacity-60"></div>
          <div className="absolute -top-2 right-0 w-12 h-8 bg-blue-100 rounded-full opacity-60"></div>
          
          {/* Main padlock icon */}
          <div className="w-32 h-32 bg-[#1C3A7A] rounded-3xl flex items-center justify-center relative">
            <div className="w-16 h-16 border-4 border-white rounded-full flex items-center justify-center">
              <div className="w-4 h-6 bg-white rounded-sm"></div>
            </div>
            {/* Lock shackle */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-12 h-8 border-4 border-[#1C3A7A] rounded-t-full bg-transparent"></div>
          </div>
          
          {/* Decorative elements */}
          <div className="absolute -bottom-2 -left-4 w-6 h-8 text-[#FAB710]">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M17,8C8,10,5.9,16.17,3.82,21.34L5.71,22L6.66,19.7C7.14,19.87,7.64,20,8,20C19,20,22,3,22,3C21,5,14,5.25,9,6.25C4,7.25,2,11.5,2,13.5C2,15.5,3.75,17.25,3.75,17.25C7,8,17,8,17,8Z"/>
            </svg>
          </div>
          <div className="absolute top-0 -right-6 w-4 h-4 text-yellow-400">★</div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 px-6">
        <h1 className="text-2xl font-bold text-center mb-2">
          <span className="text-[#FAB710]">FORGOT</span>{" "}
          <span className="text-[#1C3A7A]">PASSWORD?</span>
        </h1>
        
        <p className="text-gray-500 text-center text-sm mb-8">
          Enter your phone number associated with your account.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Phone Input */}
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-400">
              <Smartphone className="w-5 h-5" />
              <span className="text-gray-600 font-medium">+1</span>
            </div>
            <Input
              type="tel"
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="pl-20 h-14 bg-gray-50 border-gray-200 rounded-xl text-base"
              required
            />
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full h-14 bg-[#1C3A7A] hover:bg-[#15306a] text-white text-lg font-semibold rounded-xl"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Sending...
              </div>
            ) : (
              "Send"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
